import { useState } from "react";
import { FiGrid, FiPlus, FiTrash2 } from "react-icons/fi";
import { RiDraggable } from "react-icons/ri";
import {
  SortableContainer,
  SortableElement,
  SortableHandle,
} from "react-sortable-hoc";

// Drag handle component
const DragHandle = SortableHandle(() => (
  <span className="text-slate-400 hover:text-slate-600 cursor-move">
    <RiDraggable className="w-3.5 h-3.5" />
  </span>
));

// Sortable operation row
const SortableOperation = SortableElement(({ isReadOnly = false, value, onRemoveOperation, onDragStart }) => (
  <tr
    key={value.id}
    className="hover:bg-slate-50/70 transition-colors h-[20px]"
    draggable={!isReadOnly}
    onDragStart={(onDragStart && !isReadOnly) ? (e) => onDragStart(e, value.id) : undefined}
  >
    {!isReadOnly && (
      <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-800 border-r border-b border-[#cbd5e1] font-medium">
        <div className="flex items-center gap-1">
          <DragHandle />
          <span className="font-medium text-slate-800">{value.Operation}</span>
        </div>
      </td>
    )}
    <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium">
      {value.Port}
    </td>
    <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium">
      {value.CargoDescription}
    </td>
    <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-900 border-r border-b border-[#cbd5e1] tabular-nums">
      {value.CargoQty}
    </td>
    <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium tabular-nums">
      {value.RateMTday}
    </td>
    <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium tabular-nums">
      {value.TurnTimeDays}
    </td>
    <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium">
      {value.BLDate}
    </td>
    <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-800 border-r border-b border-[#cbd5e1] tabular-nums">
      {value.DemurrageRate}
    </td>
    <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-800 border-r border-b border-[#cbd5e1] tabular-nums">
      {value.DespatchRate}
    </td>
    <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium tabular-nums">
      {value.TotalTimeAllowed}
    </td>
    <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium tabular-nums">
      {value.TotalTimeUsed}
    </td>
    <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium tabular-nums">
      {value.Difference}
    </td>
    <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-900 border-r border-b border-[#cbd5e1] tabular-nums">
      {value.DemurrageDespatch}
    </td>
    {!isReadOnly && (
      <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-right border-b border-[#cbd5e1]">
        <button
          onClick={() => onRemoveOperation(value.id)}
          className="p-0.5 rounded transition-colors text-slate-400 hover:text-rose-600 hover:bg-rose-50 cursor-pointer border-none bg-transparent"
          title="Remove operation"
        >
          <FiTrash2 className="w-3 h-3" />
        </button>
      </td>
    )}
  </tr>
));

// Sortable operation list for each group
const SortableOperationList = SortableContainer(({ isReadOnly = false, operations, onRemoveOperation, onDragStart }) => {
  return (
    <tbody className="bg-white divide-y-0">
      {operations.map((op, index) => (
        <SortableOperation
          key={op.id}
          index={index}
          value={op}
          onRemoveOperation={onRemoveOperation}
          onDragStart={onDragStart}
          isReadOnly={isReadOnly}
        />
      ))}
    </tbody>
  );
});

// Single counterparty group
const Group = ({
  isReadOnly = false,
  group,
  operations,
  onOperationDrop,
  onRemoveOperation,
  onOperationSortEnd,
  onDragStart,
  handleRemoveGroup,
}) => {
  const handleDrop = (e) => {
    e.preventDefault();
    const operationId = e.dataTransfer.getData("operationId");
    if (operationId) {
      onOperationDrop(group.id, operationId);
    }
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = "move";
  };

  const groupOperations = operations
    .filter((op) => op.groupId === group.id)
    .sort((a, b) => a.order - b.order);

  return (
    <div className="w-full" onDrop={handleDrop} onDragOver={handleDragOver}>
      <div className="bg-white rounded-none border border-[#cbd5e1] overflow-hidden">
        <div className="bg-[#e2e8f0] px-3 py-1 border-b border-[#cbd5e1] flex justify-between items-center">
          <h3 className="font-medium text-[11px] text-[#1e293b] flex items-center capitalize m-0">
            <FiGrid className="mr-1.5 w-3 h-3" /> {group.name}
          </h3>
          {!isReadOnly && (
            <button
              onClick={() => handleRemoveGroup(group.id)}
              className="text-slate-400 hover:text-rose-600 p-0.5 rounded transition-colors text-[11px]"
              title="Remove group"
            >
              ✕
            </button>
          )}
        </div>
        <div className="p-2">
          {groupOperations.length > 0 ? (
            <div className="overflow-x-auto border border-[#cbd5e1] rounded-none scroll-thin">
              <table className="min-w-full border-collapse text-[11px] font-medium">
                <thead className="bg-[#e2e8f0] text-[#1e293b] h-[24px]">
                  <tr>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      Operation
                    </th>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      Port
                    </th>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      Cargo
                    </th>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      Qty
                    </th>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      Rate
                    </th>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      Turn Time
                    </th>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      B/L Date
                    </th>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      Demurrage
                    </th>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      Despatch
                    </th>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      Allowed Time
                    </th>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      Used Time
                    </th>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      Difference
                    </th>
                    <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                      Dem/Desp
                    </th>
                    {!isReadOnly && (
                      <th className="px-2 py-0.5 text-right font-medium text-[11px] align-middle border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                        Action
                      </th>
                    )}
                  </tr>
                </thead>
                <SortableOperationList
                  isReadOnly={isReadOnly}
                  operations={groupOperations}
                  onRemoveOperation={onRemoveOperation}
                  onSortEnd={({ oldIndex, newIndex }) =>
                    onOperationSortEnd(group.id, oldIndex, newIndex)
                  }
                  useDragHandle
                  lockAxis="y"
                  onDragStart={onDragStart}
                />
                <tfoot className="bg-[#f0f4f9] border-t border-[#cbd5e1] font-medium">
                  <tr>
                    <td className="px-2 py-1 text-slate-700 font-medium text-[11px] border-r border-[#cbd5e1] bg-[#f0f4f9]" colSpan={3}>
                      Total
                    </td>
                    <td className="px-2 py-1 text-slate-900 font-medium text-[11px] border-r border-[#cbd5e1] bg-[#f0f4f9] tabular-nums">
                      {groupOperations.reduce(
                        (sum, op) => sum + (parseFloat(op.CargoQty) || 0),
                        0
                      )}
                    </td>
                    <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                      {groupOperations.reduce(
                        (sum, op) => sum + (parseFloat(op.RateMTday) || 0),
                        0
                      )}
                    </td>
                    <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                      {groupOperations.reduce(
                        (sum, op) => sum + (parseFloat(op.TurnTimeDays) || 0),
                        0
                      )}
                    </td>
                    <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] font-medium"></td>
                    <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                      {groupOperations.reduce(
                        (sum, op) => sum + (parseFloat(op.DemurrageRate) || 0),
                        0
                      )}
                    </td>
                    <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                      {groupOperations.reduce(
                        (sum, op) => sum + (parseFloat(op.DespatchRate) || 0),
                        0
                      )}
                    </td>
                    <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                      {groupOperations.reduce(
                        (sum, op) =>
                          sum + (parseFloat(op.TotalTimeAllowed) || 0),
                        0
                      )}
                    </td>
                    <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                      {groupOperations.reduce(
                        (sum, op) => sum + (parseFloat(op.TotalTimeUsed) || 0),
                        0
                      )}
                    </td>
                    <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                      {groupOperations.reduce(
                        (sum, op) => sum + (parseFloat(op.Difference) || 0),
                        0
                      )}
                    </td>
                    <td className="px-2 py-1 text-slate-900 font-medium text-[11px] border-r border-[#cbd5e1] bg-[#f0f4f9] tabular-nums">
                      {groupOperations.reduce(
                        (sum, op) =>
                          sum + (parseFloat(op.DemurrageDespatch) || 0),
                        0
                      )}
                    </td>
                    {!isReadOnly && (
                      <td className="px-2 py-1 text-slate-700 text-[11px] font-medium"></td>
                    )}
                  </tr>
                </tfoot>
              </table>
            </div>
          ) : (
            <div className="text-center py-4 text-slate-400 border border-dashed border-[#cbd5e1] rounded-none text-[11px] italic">
              Drag operations here
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

// Main component
const LaytimeSettlementModel = ({
  isReadOnly,
  finalState,
  calculateStats,
  updateFinalState,
}) => {
  const [availableDefaultGroups, setAvailableDefaultGroups] = useState([]);
  const [newGroupName, setNewGroupName] = useState("");

  const handleDragStart = (e, operationId) => {
    e.dataTransfer.setData("operationId", operationId);
    e.dataTransfer.effectAllowed = "move";
  };

  const handleOperationDrop = (groupId, operationId) => {
    const updatedOperations = [...finalState.allOperations];
    const movedOpIndex = updatedOperations.findIndex(
      (op) => op.id === operationId
    );

    if (movedOpIndex === -1) return;

    const movedOp = updatedOperations[movedOpIndex];
    const newOrder = updatedOperations.filter(
      (op) => op.groupId === groupId
    ).length;

    updatedOperations[movedOpIndex] = {
      ...movedOp,
      groupId,
      order: newOrder,
    };

    if (movedOp.groupId && movedOp.groupId !== groupId) {
      const prevGroupOps = updatedOperations
        .filter((op) => op.groupId === movedOp.groupId && op.id !== operationId)
        .sort((a, b) => a.order - b.order);

      prevGroupOps.forEach((op, idx) => {
        const opIndex = updatedOperations.findIndex((o) => o.id === op.id);
        if (opIndex !== -1) {
          updatedOperations[opIndex] = { ...op, order: idx };
        }
      });
    }

    updateFinalState(
      updatedOperations,
      finalState.groups.map((g) => ({ id: g.id, name: g.name }))
    );
  };

  const handleRemoveOperation = (operationId) => {
    const updatedOperations = finalState.allOperations.map((op) =>
      op.id === operationId ? { ...op, groupId: null } : op
    );

    updateFinalState(
      updatedOperations,
      finalState.groups.map((g) => ({ id: g.id, name: g.name }))
    );
  };

  const handleOperationSortEnd = (groupId, oldIndex, newIndex) => {
    if (oldIndex === newIndex) return;

    const updatedOperations = [...finalState.allOperations];
    const groupOps = updatedOperations
      .filter((op) => op.groupId === groupId)
      .sort((a, b) => a.order - b.order);

    const [removed] = groupOps.splice(oldIndex, 1);
    groupOps.splice(newIndex, 0, removed);

    groupOps.forEach((op, index) => {
      const opIndex = updatedOperations.findIndex((o) => o.id === op.id);
      if (opIndex !== -1) {
        updatedOperations[opIndex] = { ...op, order: index };
      }
    });

    updateFinalState(
      updatedOperations,
      finalState.groups.map((g) => ({ id: g.id, name: g.name }))
    );
  };

  const handleAddGroup = () => {
    if (newGroupName.trim()) {
      const defaultGroupToAdd = availableDefaultGroups.find(
        (group) =>
          group.name.toLowerCase() === newGroupName.trim().toLowerCase()
      );

      if (defaultGroupToAdd) {
        const updatedGroups = [...finalState.groups, defaultGroupToAdd];
        updateFinalState(finalState.allOperations, updatedGroups);

        setAvailableDefaultGroups(
          availableDefaultGroups.filter(
            (group) => group.id !== defaultGroupToAdd.id
          )
        );
      } else {
        const newGroup = {
          id: `group-${Date.now()}`,
          name: newGroupName.trim(),
        };
        const updatedGroups = [...finalState.groups, newGroup];
        updateFinalState(finalState.allOperations, updatedGroups);
      }

      setNewGroupName("");
    }
  };

  const handleRemoveGroup = (groupId) => {
    const removedGroup = finalState.groups.find(
      (group) => group.id === groupId
    );

    const updatedOperations = finalState.allOperations.map((op) =>
      op.groupId === groupId ? { ...op, groupId: null } : op
    );

    const updatedGroups = finalState.groups.filter((g) => g.id !== groupId);

    updateFinalState(updatedOperations, updatedGroups);

    if (
      removedGroup &&
      !availableDefaultGroups.some((g) => g.id === removedGroup.id)
    ) {
      setAvailableDefaultGroups([...availableDefaultGroups, removedGroup]);
    }
  };

  return (
    <div className="flex flex-col gap-3 text-[11px] font-medium">
      {/* Main operations table */}
      <div className="bg-white border border-[#cbd5e1] rounded-none overflow-hidden">
        <div className="overflow-x-auto scroll-thin">
          <table className="min-w-full border-collapse text-[11px] font-medium">
            <thead className="bg-[#e2e8f0] text-[#1e293b] h-[24px]">
              <tr>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  Operation
                </th>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  Port
                </th>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  Cargo
                </th>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  Qty
                </th>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  Rate
                </th>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  Turn Time
                </th>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  B/L Date
                </th>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  Demurrage
                </th>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  Despatch
                </th>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  Allowed Time
                </th>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  Used Time
                </th>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  Difference
                </th>
                <th className="px-2 py-0.5 text-left font-medium text-[11px] align-middle border-r border-b border-[#cbd5e1] whitespace-nowrap capitalize">
                  Dem/Desp
                </th>
              </tr>
            </thead>
            <tbody className="bg-white divide-y-0">
              {finalState.ungroupedOperations.map((op) => (
                <tr
                  key={op.id}
                  draggable
                  onDragStart={(e) => handleDragStart(e, op.id)}
                  className="hover:bg-amber-50/50 transition-colors cursor-move h-[20px]"
                >
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-800 border-r border-b border-[#cbd5e1] font-medium">
                    <div className="flex items-center gap-1">
                      <DragHandle />
                      <span className="font-medium text-slate-800">{op.Operation}</span>
                    </div>
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium">
                    {op.Port}
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium">
                    {op.CargoDescription}
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-900 border-r border-b border-[#cbd5e1] tabular-nums">
                    {op.CargoQty}
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium tabular-nums">
                    {op.RateMTday}
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium tabular-nums">
                    {op.TurnTimeDays}
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium">
                    {op.BLDate}
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium tabular-nums">
                    {op.DemurrageRate}
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium tabular-nums">
                    {op.DespatchRate}
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium tabular-nums">
                    {op.TotalTimeAllowed}
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium tabular-nums">
                    {op.TotalTimeUsed}
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] text-slate-700 border-r border-b border-[#cbd5e1] font-medium tabular-nums">
                    {op.Difference}
                  </td>
                  <td className="px-2 py-0.5 whitespace-nowrap text-[11px] font-medium text-slate-900 border-r border-b border-[#cbd5e1] tabular-nums">
                    {op.DemurrageDespatch}
                  </td>
                </tr>
              ))}
            </tbody>
            <tfoot className="bg-[#f0f4f9] border-t border-[#cbd5e1] font-medium">
              <tr>
                <td className="px-2 py-1 text-slate-700 font-medium text-[11px] border-r border-[#cbd5e1] bg-[#f0f4f9]" colSpan={3}>
                  Ungrouped Total
                </td>
                <td className="px-2 py-1 text-slate-900 font-medium text-[11px] border-r border-[#cbd5e1] bg-[#f0f4f9] tabular-nums">
                  {calculateStats(finalState.ungroupedOperations).totalCargoQty}
                </td>
                <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                  {
                    calculateStats(finalState.ungroupedOperations)
                      .totalRateMTday
                  }
                </td>
                <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                  {
                    calculateStats(finalState.ungroupedOperations)
                      .totalTurnTimeDays
                  }
                </td>
                <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] font-medium"></td>
                <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                  {
                    calculateStats(finalState.ungroupedOperations)
                      .totalDemurrageRate
                  }
                </td>
                <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                  {
                    calculateStats(finalState.ungroupedOperations)
                      .totalDespatchRate
                  }
                </td>
                <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                  {
                    calculateStats(finalState.ungroupedOperations)
                      .totalTimeAllowed
                  }
                </td>
                <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                  {calculateStats(finalState.ungroupedOperations).totalTimeUsed}
                </td>
                <td className="px-2 py-1 text-slate-700 text-[11px] border-r border-[#cbd5e1] tabular-nums font-medium">
                  {
                    calculateStats(finalState.ungroupedOperations)
                      .totalDifference
                  }
                </td>
                <td className="px-2 py-1 text-slate-900 font-medium text-[11px] border-r border-[#cbd5e1] bg-[#f0f4f9] tabular-nums">
                  {
                    calculateStats(finalState.ungroupedOperations)
                      .totalDemurrageDespatch
                  }
                </td>
              </tr>
            </tfoot>
          </table>
        </div>
      </div>

      {/* Add new group section */}
      {!isReadOnly && (
        <div className="bg-white rounded-none border border-[#cbd5e1] p-3">
          <h2 className="text-[11px] font-medium capitalize text-[#1e293b] mb-2 m-0">
            Manage Port Groups
          </h2>
          <div className="flex flex-col sm:flex-row gap-2">
            <input
              type="text"
              value={newGroupName}
              onChange={(e) => setNewGroupName(e.target.value)}
              placeholder="New port group name..."
              className="flex-grow h-7 text-[11px] px-2.5 py-0.5 rounded-none border border-[#cbd5e1] text-slate-800 placeholder:text-slate-400 focus:outline-none focus:ring-1 focus:ring-inset focus:ring-amber-500 focus:bg-white capitalize bg-[#ffffd6]"
            />
            <button
              onClick={handleAddGroup}
              className="inline-flex items-center justify-center gap-1 px-3 py-1 bg-[#313C4C] text-white text-[11px] font-medium rounded-none hover:bg-[#1e293b] transition shadow-xs cursor-pointer capitalize"
            >
              <FiPlus className="w-3.5 h-3.5" />
              <span>Add Group</span>
            </button>
          </div>

          {/* Available default groups */}
          {availableDefaultGroups.length > 0 && (
            <div className="mt-2">
              <p className="text-[11px] font-medium text-slate-500 mb-1 capitalize">
                Available default groups:
              </p>
              <div className="flex flex-wrap gap-1">
                {availableDefaultGroups.map((group) => (
                  <button
                    key={group.id}
                    onClick={() => {
                      setNewGroupName(group.name);
                    }}
                    className="px-2 py-0.5 text-[11px] font-medium bg-sky-50 text-[#313C4C] hover:bg-[#313C4C] hover:text-white border border-sky-200 rounded-none transition cursor-pointer capitalize"
                  >
                    {group.name}
                  </button>
                ))}
              </div>
            </div>
          )}
        </div>
      )}

      {/* Groups section */}
      <div>
        <div className="flex justify-between items-center mb-2">
          <h2 className="text-[11px] font-medium capitalize text-[#1e293b] m-0">
            Port Groups
          </h2>
          <span className="text-[11px] font-medium text-slate-500 capitalize">
            {finalState.groups.length} groups
          </span>
        </div>
        <div className="flex flex-col space-y-3">
          {finalState.groups.map((group) => (
            <Group
              key={group.id}
              isReadOnly={isReadOnly}
              group={group}
              operations={finalState.allOperations}
              onOperationDrop={handleOperationDrop}
              onRemoveOperation={handleRemoveOperation}
              onOperationSortEnd={handleOperationSortEnd}
              onDragStart={handleDragStart}
              handleRemoveGroup={handleRemoveGroup}
            />
          ))}
        </div>
      </div>
    </div>
  );
};

export default LaytimeSettlementModel;
